import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  CHECK_LIST,
  ELEMENT_TRANSFORMERS,
  ElementTransformer,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  TextMatchTransformer,
  Transformer,
} from "@lexical/markdown";
import { $createHorizontalRuleNode, $isHorizontalRuleNode, HorizontalRuleNode } from "components/NewEditor/nodes/HorizontalRuleNode";
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table";
import { $createRangeSelection, $createTextNode, $isParagraphNode, $isTextNode, $setSelection, LexicalNode } from "lexical";
import { $createCodeNode, CodeNode } from "@lexical/code";
import { $createEquationNode, $isEquationNode, EquationNode } from "../../nodes/EquationNode/EquationNode";
import { $createSlidParagraphNode, $isSlidParagraphNode } from "components/NewEditor/nodes/SlidParagraphNode";
import { $createImageNode, $isImageNode, SlidImageNode } from "components/NewEditor/nodes/SlidImageNode/ImageNode";
import { $createSlidLinkPreviewNode, $isSlidLinkPreviewNode, SlidLinkPreviewNode } from "components/NewEditor/nodes/SlidLinkPreviewNode";

export const CODE: TextMatchTransformer = {
  dependencies: [CodeNode],
  regExp: /^```$/,
  trigger: "`",
  replace: (textNode, match) => {
    const codeNode = $createCodeNode();
    const parent = textNode.getParent();
    if (parent) {
      parent.replace(codeNode);
    }
  },
  type: "text-match",
};

export const HR: TextMatchTransformer = {
  dependencies: [HorizontalRuleNode],
  regExp: /^(---|\*\*\*|___|—-)$/,
  trigger: "-",
  replace: (textNode, match) => {
    const line = $createHorizontalRuleNode();
    const parent = textNode.getParent();
    if (parent) {
      parent.replace(line);
    }
    const newParagraphNode = $createSlidParagraphNode();
    line.insertAfter(newParagraphNode);
    const newSelection = $createRangeSelection();
    newSelection.anchor.set(newParagraphNode.getKey(), 0, "element");
    newSelection.focus.set(newParagraphNode.getKey(), 0, "element");
    $setSelection(newSelection);
  },
  type: "text-match",
};

export const EQUATION: TextMatchTransformer = {
  dependencies: [EquationNode],
  export: (node) => {
    if (!$isEquationNode(node)) {
      return null;
    }

    return `$${node.getEquation()}$`;
  },
  importRegExp: /\$([^\$]+?)\$/,
  regExp: /\$([^\$]+?)\$$/,
  replace: (textNode, match) => {
    const [, equation] = match;
    const equationNode = $createEquationNode(equation, true);
    textNode.replace(equationNode);
  },
  trigger: "$",
  type: "text-match",
};

// Very primitive table setup
const TABLE_ROW_REG_EXP = /^(?:\|)(.+)(?:\|)[ \t]*$/;
const TABLE_ROW_DIVIDER_REG_EXP = /^(\|[ \t]*:?[-]*:?[ \t]*)+\|[ \t]*$/;

export const TABLE: ElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (node: LexicalNode) => {
    if (!$isTableNode(node)) {
      return null;
    }

    const output: string[] = [];

    for (const row of node.getChildren()) {
      const rowOutput: string[] = [];
      if (!$isTableRowNode(row)) {
        continue;
      }

      let isHeaderRow = false;
      for (const cell of row.getChildren()) {
        // It's TableCellNode so it's just to make flow happy
        if ($isTableCellNode(cell)) {
          rowOutput.push($convertToMarkdownString(PLAYGROUND_TRANSFORMERS, cell).replace(/\n/g, "\\n"));
          if (cell.__headerState === TableCellHeaderStates.ROW) {
            isHeaderRow = true;
          }
        }
      }

      output.push(`| ${rowOutput.join(" | ")} |`);
      if (isHeaderRow) {
        output.push(`| ${rowOutput.map((_) => "---").join(" | ")} |`);
      }
    }

    return output.join("\n");
  },
  regExp: TABLE_ROW_REG_EXP,
  replace: (parentNode, _1, match) => {
    // Header row
    if (TABLE_ROW_DIVIDER_REG_EXP.test(match[0])) {
      const table = parentNode.getPreviousSibling();
      if (!table || !$isTableNode(table)) {
        return;
      }

      const rows = table.getChildren();
      const lastRow = rows[rows.length - 1];
      if (!lastRow || !$isTableRowNode(lastRow)) {
        return;
      }

      // Add header state to row cells
      lastRow.getChildren().forEach((cell) => {
        if (!$isTableCellNode(cell)) {
          return;
        }
        cell.toggleHeaderStyle(TableCellHeaderStates.ROW);
      });

      // Remove line
      parentNode.remove();
      return;
    }

    const matchCells = mapToTableCells(match[0]);

    if (matchCells == null) {
      return;
    }

    const rows = [matchCells];
    let sibling = parentNode.getPreviousSibling();
    let maxCells = matchCells.length;

    while (sibling) {
      if (!$isParagraphNode(sibling) || !$isSlidParagraphNode(sibling)) {
        break;
      }

      if (sibling.getChildrenSize() !== 1) {
        break;
      }

      const firstChild = sibling.getFirstChild();

      if (!$isTextNode(firstChild)) {
        break;
      }

      const cells = mapToTableCells(firstChild.getTextContent());

      if (cells == null) {
        break;
      }

      maxCells = Math.max(maxCells, cells.length);
      rows.unshift(cells);
      const previousSibling = sibling.getPreviousSibling();
      sibling.remove();
      sibling = previousSibling;
    }

    const table = $createTableNode();

    for (const cells of rows) {
      const tableRow = $createTableRowNode();
      table.append(tableRow);

      for (let i = 0; i < maxCells; i++) {
        tableRow.append(i < cells.length ? cells[i] : $createTableCell(""));
      }
    }

    const previousSibling = parentNode.getPreviousSibling();
    if ($isTableNode(previousSibling) && getTableColumnsSize(previousSibling) === maxCells) {
      previousSibling.append(...table.getChildren());
      parentNode.remove();
    } else {
      parentNode.replace(table);
    }

    table.selectEnd();
  },
  type: "element",
};

function getTableColumnsSize(table: TableNode) {
  const row = table.getFirstChild();
  return $isTableRowNode(row) ? row.getChildrenSize() : 0;
}

const $createTableCell = (textContent: string): TableCellNode => {
  textContent = textContent.replace(/\\n/g, "\n");
  const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
  $convertFromMarkdownString(textContent, PLAYGROUND_TRANSFORMERS, cell);
  return cell;
};

const mapToTableCells = (textContent: string): Array<TableCellNode> | null => {
  const match = textContent.match(TABLE_ROW_REG_EXP);
  if (!match || !match[1]) {
    return null;
  }
  return match[1].split("|").map((text) => $createTableCell(text));
};

const IMAGE: ElementTransformer = {
  dependencies: [SlidImageNode],
  export: (node) => {
    if (!$isImageNode(node)) {
      return null;
    }

    return `![${node.getSrc()}](${node.getSrc()})`;
  },
  regExp: /^\!\[(.*?)\]\((.*?)\)$/,
  replace: (parentNode, _1, match) => {
    const [, alt, src] = match;
    const imageNode = $createImageNode({
      originalImageSrc: src,
      src: src,
      isUploaded: false,
      sourceType: "fileUpload",
    });
    parentNode.replace(imageNode);
  },
  type: "element",
};

const LINK_PREVIEW: ElementTransformer = {
  dependencies: [SlidLinkPreviewNode],
  export: (node) => {
    if (!$isSlidLinkPreviewNode(node)) {
      return null;
    }

    return `[${node.getTextContent()}](${node.getTextContent()})`;
  },
  regExp: /^\[([^\]]+)\]\((.*?)\)$/,
  replace: (parentNode, _1, match) => {
    const [, title, url] = match;
    const linkPreviewNode = $createSlidParagraphNode();
    linkPreviewNode.append($createTextNode(url));
    parentNode.replace(linkPreviewNode);
  },
  type: "element",
};

export const PLAYGROUND_TRANSFORMERS: Array<Transformer> = [TABLE, HR, CHECK_LIST, CODE, LINK_PREVIEW, IMAGE, ...ELEMENT_TRANSFORMERS, ...TEXT_FORMAT_TRANSFORMERS, ...TEXT_MATCH_TRANSFORMERS];
